/**
 * Kardex de un insumo y export de movimientos (B6 / RES-34).
 *
 * El backend ya guarda `StockBefore` / `StockAfter` en cada movimiento, así que el saldo
 * corriente no se recalcula: se lee. Estas funciones son las que arman la vista del kardex
 * (saldo de apertura, saldo de cierre y si el kardex "cierra" contra el stock actual) y la
 * exportación a CSV con los filtros que el usuario tenga puestos.
 */

// `Number(null)` es 0, así que 'sin dato' se descarta antes de convertir
const num = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Resumen del kardex de un insumo a partir de sus movimientos.
 *
 * Los movimientos llegan del backend más recientes primero. El saldo de cierre es el
 * `StockAfter` del más reciente y el de apertura el `StockBefore` del más viejo.
 *
 * @param {Array<Object>} movements - Movimientos del insumo, más recientes primero
 * @param {number|null} currentStock - Stock actual del insumo en el local
 * @returns {{opening: number|null, closing: number|null, movements: number, net: number|null, currentStock: number|null, balances: boolean|null}} Resumen
 */
export function kardexSummary(movements, currentStock) {
  const list = Array.isArray(movements) ? movements : [];
  if (list.length === 0) {
    const stock = num(currentStock);
    return {
      opening: stock,
      closing: stock,
      movements: 0,
      net: 0,
      currentStock: stock,
      balances: stock === null ? null : true,
    };
  }

  const closing = num(list[0].StockAfter);
  const opening = num(list[list.length - 1].StockBefore);
  const net = list.reduce((sum, movement) => sum + (num(movement.Quantity) || 0), 0);
  const stock = num(currentStock);

  return {
    opening,
    closing,
    movements: list.length,
    net: Math.round(net * 1e6) / 1e6,
    currentStock: stock,
    // El kardex cierra cuando el saldo del último movimiento es el stock de hoy. Solo tiene
    // sentido comparar si no hay filtros que corten el historial por arriba (lo decide la página).
    balances: closing === null || stock === null ? null : Math.abs(closing - stock) < 1e-6,
  };
}

/** Escapa un valor para CSV (comillas dobles, comas y saltos de línea). */
const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Columnas del export, en orden. */
export const MOVEMENT_CSV_COLUMNS = [
  'fecha',
  'tipo',
  'insumo',
  'cantidad',
  'unidad',
  'stockAnterior',
  'stockPosterior',
  'motivo',
  'referencia',
  'usuario',
];

/**
 * CSV de una lista de movimientos, con los nombres de insumo ya resueltos.
 * @param {Array<Object>} movements - Movimientos
 * @param {{itemName: function(string): string, itemUnit: function(string): string}} resolve - Resolutores de nombre y unidad
 * @returns {string} Contenido del CSV
 */
export function movementsToCsv(movements, { itemName, itemUnit } = {}) {
  const rows = [MOVEMENT_CSV_COLUMNS.join(',')];
  for (const movement of Array.isArray(movements) ? movements : []) {
    rows.push(
      [
        movement.CreatedAt || '',
        movement.Type || '',
        itemName ? itemName(movement.ItemId) : movement.ItemId,
        movement.Quantity ?? '',
        itemUnit ? itemUnit(movement.ItemId) : '',
        movement.StockBefore ?? '',
        movement.StockAfter ?? '',
        movement.Reason || '',
        movement.RefId || '',
        movement.UserId || '',
      ]
        .map(csvCell)
        .join(',')
    );
  }
  return rows.join('\n');
}

/**
 * Versión de un texto libre segura para nombre de archivo: sin acentos, sin caracteres que
 * rompan el download en algún SO, espacios a guion.
 * @param {string} text - Texto a normalizar
 * @returns {string} Slug
 */
function slugify(text) {
  return String(text)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // acentos
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Nombre de archivo del export, con los filtros aplicados y la fecha del día.
 *
 * RES-71 (TC-INVB-30): el filtro por insumo se identificaba con su UUID crudo — un admin no
 * podía saber a qué insumo correspondía el archivo con solo mirar el nombre. Con `itemName` se
 * arma un slug legible; sin nombre resuelto cae al UUID (mejor un nombre de archivo con el ID
 * que uno sin ningún rastro de qué se exportó).
 *
 * @param {{itemId?: string, type?: string, from?: string, to?: string}} filters - Filtros
 * @param {Date} [now] - Fecha (inyectable para tests)
 * @param {string} [itemName] - Nombre legible del insumo filtrado, si se conoce
 * @returns {string} Nombre de archivo
 */
export function csvFileName(filters = {}, now = new Date(), itemName = '') {
  const parts = ['movimientos'];
  if (filters.itemId) {
    const slug = slugify(itemName);
    parts.push(slug || filters.itemId);
  }
  if (filters.type) parts.push(filters.type.toLowerCase());
  if (filters.from) parts.push(`desde-${filters.from}`);
  if (filters.to) parts.push(`hasta-${filters.to}`);
  parts.push(now.toISOString().slice(0, 10));
  return `${parts.join('_')}.csv`;
}

/**
 * Dispara la descarga del CSV en el navegador.
 * @param {string} content - Contenido del CSV
 * @param {string} fileName - Nombre del archivo
 * @returns {void}
 */
export function downloadCsv(content, fileName) {
  // BOM para que Excel abra bien los acentos
  const blob = new Blob([`﻿${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
